import { cn } from "@krea/shared/utils";

export type CreditScoreColor = "GREEN" | "YELLOW" | "RED";

type CreditScoreStyle = {
  label: string;
  bg: string;
  text: string;
  dot: string;
  border: string | null;
};

// These lender-facing risk labels are intentionally kept as English literals
// (consistent with the rest of this component); the lender portal UI is not
// localised, so they are not routed through i18next.
const CREDIT_SCORE_STYLES: Record<CreditScoreColor, CreditScoreStyle> = {
  // biome-ignore lint/style/useNamingConvention: backend enum value
  GREEN: {
    label: "Low risk",
    bg: "bg-mint-100",
    text: "text-mint-500",
    dot: "bg-mint-500",
    border: "border border-mint-400",
  },
  // biome-ignore lint/style/useNamingConvention: backend enum value
  YELLOW: {
    label: "Medium risk",
    bg: "bg-orange-50",
    text: "text-orange",
    dot: "bg-orange",
    border: "border border-orange-200",
  },
  // biome-ignore lint/style/useNamingConvention: backend enum value
  RED: {
    label: "High risk",
    bg: "bg-red-50",
    text: "text-red-700",
    dot: "bg-red-500",
    border: "border border-red-200",
  },
};

const NO_INFORMATION_STYLE: CreditScoreStyle = {
  label: "No information",
  bg: "bg-gray-100",
  text: "text-gray-600",
  dot: "bg-gray-400",
  border: "border border-gray-300",
};

/**
 * SE UC alphabetic rating classes. When a Swedish company has a non-numeric
 * credit rating, the backend sends the class letter as `creditScoreStatus`
 * (instead of a GREEN/YELLOW/RED colour). It renders as a distinct neutral
 * status — the class meaning, in a primary-tinted indicator (KREA-4245).
 */
const UC_STATUS_LABELS = new Map<string, string>([
  ["A", "Deregistered"],
  ["B", "Not determined"],
  ["C", "Commission"],
  ["F", "Merger"],
  ["I", "No active business registered"],
  ["K", "Bankruptcy"],
  ["L", "Liquidation"],
  ["N", "Numbered company"],
  ["O", "Composition / reorganization"],
  ["U", "Distraint"],
  ["Ö", "Transferred business"],
  ["X", "Not rated"],
]);

const STATUS_STYLE: Omit<CreditScoreStyle, "label"> = {
  bg: "bg-primary-100",
  text: "text-primary-400",
  dot: "bg-primary-200",
  border: "border border-primary-200",
};

/**
 * Resolve the credit-risk presentation for a backend colour value. A missing /
 * unknown colour (null, undefined or an unrecognised value) resolves to the
 * neutral "No information" style instead of defaulting to a risk level
 * (KREA-4245).
 */
export function getCreditScoreStyle(color?: string | null): CreditScoreStyle {
  if (!color) {
    return NO_INFORMATION_STYLE;
  }

  return CREDIT_SCORE_STYLES[color as CreditScoreColor] ?? NO_INFORMATION_STYLE;
}

/** Human-readable risk label for a backend colour value. */
export function getCreditScoreLabel(color?: string | null): string {
  return getCreditScoreStyle(color).label;
}

/** Human-readable label for a SE UC alphabetic rating class (e.g. "K" → "Bankruptcy"). */
export function getCreditScoreStatusLabel(status: string): string {
  return UC_STATUS_LABELS.get(status) ?? "Unknown status";
}

/**
 * Credit-risk tag shown to lenders on the application detail view. Renders a
 * coloured pill with a human-readable risk label. When `status` is set (a SE UC
 * alphabetic class) it takes precedence and renders the class meaning with a
 * neutral primary-tinted style.
 */
export function CreditScorePill({
  color,
  status,
}: {
  color?: string | null;
  status?: string | null;
}) {
  const { bg, text, dot, label, border } = status
    ? { ...STATUS_STYLE, label: getCreditScoreStatusLabel(status) }
    : getCreditScoreStyle(color);

  return (
    <div
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full pr-3 py-1 text-sm font-medium",
        status ? "pl-1" : "pl-3",
        border,
        bg,
        text,
      )}
    >
      {status ? (
        <span
          className={cn(
            "h-5 w-5 rounded-full text-center text-[10px] font-semibold ",
            dot,
          )}
          aria-hidden="true"
        >
          {status}
        </span>
      ) : (
        <span className={cn("h-2 w-2 rounded-full", dot)} aria-hidden="true" />
      )}
      {label}
    </div>
  );
}
